import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
// ChartHelpButton removed for preset view (no title icon)
import { getCurrentISOWeek, getCurrentISOYear, formatDateRange } from '../../utils/analyticsHelpers';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { usePersistWidgetSnapshot } from '../../hooks/usePersistWidgetSnapshot';
import ChartFullscreenTitleControl from '../analytics/ChartFullscreenTitleControl';
import { PresetChartTitle, PresetFilterStrip, presetChartBodyClass, PRESET_FILTER_OPEN_PT, PRESET_PLOT_CAPTION_PAD } from '../analytics/PresetChartChrome';
import { singleSelectionLabel } from '../../../utils/filterLabels';

/**
 * Smart Price Tracker - Shows weekly price categories (Low/Balanced/High)
 */
const WEEK_BUTTON_MIN_WIDTH = 90; // Minimum width per week button in pixels
const WEEK_BUTTON_GAP = 8; // Gap between buttons in pixels
const ARROW_BUTTON_WIDTH = 44; // Width of arrow buttons including padding
const FIELD_SEPARATOR = ' · ';

const formatItemLabel = (item) => {
  return ['type', 'variety', 'item_size', 'grade', 'package']
    .map(field => item[field])
    .filter(val => val && val.trim() !== '' && val !== 'N/A')
    .join(FIELD_SEPARATOR);
};

const ClusterChart = ({
  data = [],
  commodity = '',
  forceFiltersOpen = false,
  presetScorecards = null,
  presetDescription = '',
  analyticsFullscreen = null,
  presetSingleChartFullscreen = false,
}) => {
  const { t } = useTranslation();
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  // Collapsed by default. The preset view keeps filters open from lg up via the
  // CSS gate on the strip below; smaller screens stay behind the Filters toggle.
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const [visibleWeeksCount, setVisibleWeeksCount] = useState(5);
  const weekSelectorRef = useRef(null);
  const isManualNavigation = useRef(false);

  // Get current week/year
  const currentISOWeek = useMemo(() => getCurrentISOWeek(), []);
  const currentISOYear = useMemo(() => getCurrentISOYear(), []);
  const defaultSelectedWeek = useMemo(() => Math.max(1, currentISOWeek - 1), [currentISOWeek]);

  // State
  const [selectedWeek, setSelectedWeek] = useState(defaultSelectedWeek);
  const [selectedYear, setSelectedYear] = useState(currentISOYear);
  const [cardExpanded, setCardExpanded] = useState({
    'Lower than usual': false,
    'Usual': false,
    'Higher than usual': false,
  });

  // Filter states
  const [selectedTypes, setSelectedTypes] = useState(['Conventional', 'Organic']);
  const [selectedVarieties, setSelectedVarieties] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);

  // Modal states
  const [yearModalOpen, setYearModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [varietyModalOpen, setVarietyModalOpen] = useState(false);
  const [sizeModalOpen, setSizeModalOpen] = useState(false);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [packageModalOpen, setPackageModalOpen] = useState(false);

  const firestoreClusterRef = useRef(false);
  const skipDropdownInitRef = useRef(false);

  const chartData = data && Array.isArray(data) ? data : [];

  // When commodity changes, clear multi-select filters so we re-default to "all" within the new commodity scope.
  // This prevents stale/invalid selections from producing "0 selected" while options exist.
  useLayoutEffect(() => {
    skipDropdownInitRef.current = false;
    firestoreClusterRef.current = false;
    setSelectedVarieties([]);
    setSelectedSizes([]);
    setSelectedGrades([]);
    setSelectedPackages([]);
  }, [commodity]);

  // Get available years
  const availableYears = useMemo(() => {
    const years = [...new Set(chartData.map((d) => d.iso_year).filter(Boolean))].sort((a, b) => b - a);
    return years;
  }, [chartData]);

  // Get dropdown options
  const dropdownOptions = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { types: [], varieties: [], sizes: [], grades: [], packages: [] };
    }

    const filterValid = (arr) => arr.filter((v) => v && v.trim() !== '' && v !== 'N/A');

    return {
      types: [...new Set(filterValid(chartData.map((d) => d.type)))].sort(),
      varieties: [...new Set(filterValid(chartData.map((d) => d.variety)))].sort(),
      sizes: [...new Set(filterValid(chartData.map((d) => d.item_size)))].sort(),
      grades: [...new Set(filterValid(chartData.map((d) => d.grade)))].sort(),
      packages: [...new Set(filterValid(chartData.map((d) => d.package)))].sort(),
    };
  }, [chartData]);

  // Stable deps so effects don't re-fire on new array/object identity with the same contents
  const availableYearsKey = useMemo(() => availableYears.join('\0'), [availableYears]);
  const dropdownOptionsSig = useMemo(() => {
    const o = dropdownOptions;
    return [o.types, o.varieties, o.sizes, o.grades, o.packages].map((a) => a.join('|')).join('\n');
  }, [dropdownOptions]);

  const hydrateCluster = useCallback(
    (s) => {
      firestoreClusterRef.current = true;
      skipDropdownInitRef.current = true;
      if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
      // Do not restore visibleStartIndex / visibleWeeksCount from Firestore: ResizeObserver + layout
      // recalculate visibleWeeksCount; re-applying saved values fights the observer and churns
      // persist fingerprint (5↔6 buttons) → hydrate loops. Auto-scroll effect aligns start to selectedWeek.
      if (typeof s.selectedWeek === 'number') setSelectedWeek(s.selectedWeek);
      if (typeof s.selectedYear === 'number' && availableYears.includes(s.selectedYear)) {
        setSelectedYear(s.selectedYear);
      }
      if (s.cardExpanded && typeof s.cardExpanded === 'object') setCardExpanded(s.cardExpanded);
      // For multi-select filters: if saved selection is missing/invalid/empty, default to ALL options.
      const pickMultiOrAll = (arr, allowed) => {
        if (!allowed.length) return [];
        if (!Array.isArray(arr)) return allowed;
        const next = arr.filter((x) => allowed.includes(x));
        return next.length ? next : allowed;
      };

      const t = pickMultiOrAll(s.selectedTypes, dropdownOptions.types);
      setSelectedTypes(t);
      setSelectedVarieties(pickMultiOrAll(s.selectedVarieties, dropdownOptions.varieties));
      setSelectedSizes(pickMultiOrAll(s.selectedSizes, dropdownOptions.sizes));
      setSelectedGrades(pickMultiOrAll(s.selectedGrades, dropdownOptions.grades));
      setSelectedPackages(pickMultiOrAll(s.selectedPackages, dropdownOptions.packages));
    },
    [availableYears, dropdownOptions, commodity, chartData.length]
  );

  const snapshotCluster = useCallback(() => {
    const snap = {
      filtersExpanded,
      selectedWeek,
      selectedYear,
      cardExpanded,
      selectedTypes: [...selectedTypes].sort(),
      selectedVarieties: [...selectedVarieties].sort(),
      selectedSizes: [...selectedSizes].sort(),
      selectedGrades: [...selectedGrades].sort(),
      selectedPackages: [...selectedPackages].sort(),
    };
    return snap;
  }, [
    filtersExpanded,
    selectedWeek,
    selectedYear,
    cardExpanded,
    selectedTypes,
    selectedVarieties,
    selectedSizes,
    selectedGrades,
    selectedPackages,
  ]);

  const persistDepsKey = useMemo(() => JSON.stringify({
    filtersExpanded,
    selectedWeek,
    selectedYear,
    cardExpanded,
    selectedTypes,
    selectedVarieties,
    selectedSizes,
    selectedGrades,
    selectedPackages,
  }), [
    filtersExpanded,
    selectedWeek,
    selectedYear,
    cardExpanded,
    selectedTypes,
    selectedVarieties,
    selectedSizes,
    selectedGrades,
    selectedPackages,
  ]);

  const persistDeps = useMemo(() => [persistDepsKey], [persistDepsKey]);

  usePersistWidgetSnapshot(
    'cluster',
    commodity,
    chartData.length > 0,
    hydrateCluster,
    snapshotCluster,
    persistDeps
  );

  // Initialize filters
  useEffect(() => {
    if (skipDropdownInitRef.current) {
      skipDropdownInitRef.current = false;
      return;
    }
    if (dropdownOptions.varieties.length > 0 && selectedVarieties.length === 0) {
      setSelectedVarieties(dropdownOptions.varieties);
    }
    if (dropdownOptions.sizes.length > 0 && selectedSizes.length === 0) {
      setSelectedSizes(dropdownOptions.sizes);
    }
    if (dropdownOptions.grades.length > 0 && selectedGrades.length === 0) {
      setSelectedGrades(dropdownOptions.grades);
    }
    if (dropdownOptions.packages.length > 0 && selectedPackages.length === 0) {
      setSelectedPackages(dropdownOptions.packages);
    }
    // dropdownOptionsSig changes only when option lists change (not array identity churn)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read dropdownOptions from closure when sig changes
  }, [dropdownOptionsSig]);

  // Update year when data changes
  useEffect(() => {
    if (firestoreClusterRef.current) {
      firestoreClusterRef.current = false;
      return;
    }
    if (availableYears.length > 0) {
      const next = Math.max(...availableYears);
      setSelectedYear(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- availableYearsKey encodes iso_year set
  }, [availableYearsKey]);

  // Extract unique weeks
  const weeks = useMemo(() => {
    const uniqueWeeks = new Map();

    if (selectedYear === currentISOYear) {
      // For current year: show all 52 weeks
      // - Weeks <= currentISOWeek: use current year data (e.g., 2026)
      // - Weeks > currentISOWeek: use previous year data as placeholders (e.g., 2025)

      // First pass: add current year data for weeks <= currentISOWeek
      chartData.forEach((item) => {
        const weekNum = Number(item.iso_week_number);
        const itemYear = Number(item.iso_year);

        if (itemYear === selectedYear && weekNum <= currentISOWeek) {
          if (!uniqueWeeks.has(weekNum)) {
            uniqueWeeks.set(weekNum, {
              weekNumber: weekNum,
              start: item.iso_week_start,
              end: item.iso_week_end,
              year: itemYear,
            });
          }
        }
      });

      // Second pass: add previous year data for weeks > currentISOWeek (future placeholders)
      chartData.forEach((item) => {
        const weekNum = Number(item.iso_week_number);
        const itemYear = Number(item.iso_year);

        if (itemYear === selectedYear - 1 && weekNum > currentISOWeek) {
          if (!uniqueWeeks.has(weekNum)) {
            uniqueWeeks.set(weekNum, {
              weekNumber: weekNum,
              start: item.iso_week_start,
              end: item.iso_week_end,
              year: itemYear,
            });
          }
        }
      });
    } else {
      // For previous years: only show that year's data
      chartData
        .filter((item) => Number(item.iso_year) === selectedYear)
        .forEach((item) => {
          const weekNum = Number(item.iso_week_number);
          if (!uniqueWeeks.has(weekNum)) {
            uniqueWeeks.set(weekNum, {
              weekNumber: weekNum,
              start: item.iso_week_start,
              end: item.iso_week_end,
              year: Number(item.iso_year),
            });
          }
        });
    }

    return Array.from(uniqueWeeks.values())
      .sort((a, b) => a.weekNumber - b.weekNumber)
      .map((weekData) => ({
        weekNumber: weekData.weekNumber,
        startDate: weekData.start,
        endDate: weekData.end,
        year: weekData.year,
      }));
  }, [chartData, selectedYear, currentISOYear, currentISOWeek]);

  // Calculate how many weeks can fit based on container width
  const calculateVisibleWeeks = useCallback(() => {
    if (!weekSelectorRef.current) return;
    const containerWidth = weekSelectorRef.current.offsetWidth;
    // Available width = container - 2 arrow buttons - gaps
    const availableWidth = containerWidth - (2 * ARROW_BUTTON_WIDTH) - (2 * WEEK_BUTTON_GAP);
    // Calculate how many buttons fit (including gaps between them)
    const count = Math.max(1, Math.floor((availableWidth + WEEK_BUTTON_GAP) / (WEEK_BUTTON_MIN_WIDTH + WEEK_BUTTON_GAP)));
    setVisibleWeeksCount(count);
  }, []);

  // Resize observer to recalculate on container resize
  useEffect(() => {
    calculateVisibleWeeks();
    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleWeeks();
    });
    if (weekSelectorRef.current) {
      resizeObserver.observe(weekSelectorRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [calculateVisibleWeeks]);

  // Get visible weeks slice for arrow navigation (continuous/circular)
  const visibleWeeks = useMemo(() => {
    if (weeks.length === 0) return [];
    if (weeks.length <= visibleWeeksCount) return weeks;

    // Handle circular wrap-around
    const result = [];
    for (let i = 0; i < visibleWeeksCount; i++) {
      const index = (visibleStartIndex + i) % weeks.length;
      result.push(weeks[index]);
    }
    return result;
  }, [weeks, visibleStartIndex, visibleWeeksCount]);

  // Scroll handler for arrow navigation (continuous/circular)
  const scrollWeeks = (direction) => {
    if (weeks.length === 0) return;
    // Mark this as manual navigation to prevent auto-scroll from interfering
    isManualNavigation.current = true;
    setVisibleStartIndex((prev) => {
      let newIndex = prev + direction;
      // Wrap around: if negative, go to end; if past end, go to start
      if (newIndex < 0) {
        newIndex = weeks.length + newIndex;
      } else if (newIndex >= weeks.length) {
        newIndex = newIndex % weeks.length;
      }
      return newIndex;
    });
    // Reset the flag after a short delay to allow the state to settle
    setTimeout(() => {
      isManualNavigation.current = false;
    }, 100);
  };

  // Auto-scroll to keep selected week visible (only when week is selected, not during arrow navigation)
  useEffect(() => {
    // Skip auto-scroll if this was triggered by manual arrow navigation
    if (isManualNavigation.current) return;
    if (weeks.length === 0) return;

    const selectedIndex = weeks.findIndex((w) => w.weekNumber === selectedWeek);
    if (selectedIndex === -1) return;

    // Check if selected week is currently visible (accounting for circular display)
    const isVisible = visibleWeeks.some((w) => w.weekNumber === selectedWeek);

    // Only auto-scroll if the selected week is not visible
    if (!isVisible) {
      setVisibleStartIndex(selectedIndex);
    }
  }, [selectedWeek, weeks, visibleWeeks]);

  // Filter data for selected week
  const weekData = useMemo(() => {
    let targetYear = selectedYear;
    if (selectedYear === currentISOYear && selectedWeek > currentISOWeek) {
      targetYear = selectedYear - 1;
    }

    let filtered = chartData.filter(
      (item) => Number(item.iso_week_number) === selectedWeek && Number(item.iso_year) === targetYear
    );

    // Apply filters
    if (selectedTypes.length < dropdownOptions.types.length) {
      filtered = filtered.filter((item) => !item.type || selectedTypes.includes(item.type));
    }
    if (selectedVarieties.length < dropdownOptions.varieties.length) {
      filtered = filtered.filter((item) => selectedVarieties.includes(item.variety));
    }
    if (selectedSizes.length < dropdownOptions.sizes.length) {
      filtered = filtered.filter((item) => selectedSizes.includes(item.item_size));
    }
    if (selectedGrades.length < dropdownOptions.grades.length) {
      filtered = filtered.filter((item) => selectedGrades.includes(item.grade));
    }
    if (selectedPackages.length < dropdownOptions.packages.length) {
      filtered = filtered.filter((item) => selectedPackages.includes(item.package));
    }

    // Group by price category
    const grouped = {
      'Lower than usual': [],
      'Usual': [],
      'Higher than usual': [],
    };

    filtered.forEach((item) => {
      if (grouped[item.price_category]) {
        grouped[item.price_category].push(item);
      }
    });

    return grouped;
  }, [chartData, selectedWeek, selectedYear, currentISOYear, currentISOWeek, selectedTypes, selectedVarieties, selectedSizes, selectedGrades, selectedPackages, dropdownOptions]);

  const isWeekInFuture = (weekNumber, weekYear) => {
    if (selectedYear !== currentISOYear) return false;
    return weekNumber > currentISOWeek || weekYear === selectedYear - 1;
  };

  const selectedWeekInfo = weeks.find((w) => w.weekNumber === selectedWeek);

  const handleMultiSelect = (item, selected, setSelected) => {
    if (selected.includes(item)) {
      setSelected(selected.filter((i) => i !== item));
    } else {
      setSelected([...selected, item]);
    }
  };

  const categoryConfig = [
    { key: 'Lower than usual', titleKey: 'charts.smartPriceTracker.low', borderColor: '#EFA49E', bgColor: 'rgba(239, 164, 158, 0.1)', priceColor: '#EFA49E' },
    { key: 'Usual', titleKey: 'charts.smartPriceTracker.balanced', borderColor: '#E2726A', bgColor: 'rgba(226, 114, 106, 0.1)', priceColor: '#E2726A' },
    { key: 'Higher than usual', titleKey: 'charts.smartPriceTracker.high', borderColor: '#DC3E34', bgColor: 'rgba(220, 62, 52, 0.1)', priceColor: '#DC3E34' },
  ];

  const helpDescription = "Weekly price classification showing whether prices are Low, Balanced, or High based on historical patterns. Use filters to drill down by type, variety, size, grade, and package.";

  // Single-chart fullscreen: below lg the overlay is the only scroll container,
  // so these boxes grow with their content (week selector + all category cards)
  // instead of clipping. See the `.preset-fullscreen-*` block in index.css.
  const fsFill = presetSingleChartFullscreen ? ' preset-fullscreen-fill' : '';

  if (!chartData || chartData.length === 0) {
    return (
      <div className="h-full flex flex-col bg-transparent">
        <PresetChartTitle
          forceFiltersOpen={forceFiltersOpen}
          title={t('charts.smartPriceTracker.title')}
          theme={theme}
          fontSize={fontSize}
          className={forceFiltersOpen ? 'px-4 pt-4' : 'border-b py-2'}
        />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-center" style={{ fontSize, color: theme.noDataText }}>
            {t('charts.smartPriceTracker.noData')}<br />{t('charts.selectAnotherTimeframe')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        (forceFiltersOpen
          ? presetSingleChartFullscreen
            ? 'w-full h-full min-h-0 flex-1' + fsFill
            : 'w-full h-full min-h-0'
          : 'h-full') + ' flex flex-col bg-transparent'
      }
    >
      <div className={`flex-1 flex flex-col min-h-0 overflow-hidden${fsFill} ${presetChartBodyClass(forceFiltersOpen, presetSingleChartFullscreen)}`}>
      {/* Title */}
      <PresetChartTitle
        forceFiltersOpen={forceFiltersOpen}
        title={t('charts.smartPriceTracker.title')}
        theme={theme}
        fontSize={fontSize}
        right={
          <ChartFullscreenTitleControl
            analyticsFullscreen={analyticsFullscreen}
            presetSingleChartFullscreen={presetSingleChartFullscreen}
          />
        }
      />

      {/* Filters / Cards / Chart */}
        {/* Filters */}
        <div className="flex-shrink-0">
          {/* Filters Header — collapsible in the widget dashboard, and below lg
              in the preset view too; lg:hidden once forceFiltersOpen since
              desktop preset keeps the strip open via the CSS gate below. */}
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className={`w-full flex items-center justify-between py-2 border-b ${forceFiltersOpen ? 'lg:hidden' : ''}`}
            style={{ borderColor: theme.borderSecondary }}
          >
            <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>{t('filters.filters')}</span>
            {filtersExpanded ? <ChevronUp size={16} style={{ color: theme.secondaryText }} /> : <ChevronDown size={16} style={{ color: theme.secondaryText }} />}
          </button>

          {/* Filter Dropdowns */}
          {(forceFiltersOpen || filtersExpanded) && (
            <div className={forceFiltersOpen ? (filtersExpanded ? PRESET_FILTER_OPEN_PT : 'max-lg:hidden') : undefined}>
            <PresetFilterStrip
              forceFiltersOpen={forceFiltersOpen}
              presetSingleChartFullscreen={presetSingleChartFullscreen}
              analyticsFullscreen={analyticsFullscreen}
              titleHasFullscreenControl
              gridClassName="pt-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2"
            >
            <FilterDropdown
              label={t('filters.year')}
              value={selectedYear}
              onClick={() => setYearModalOpen(true)}
            compact={forceFiltersOpen}
            />

            {dropdownOptions.types.length > 0 && (
              <FilterDropdown
                label={t('filters.type')}
                value={singleSelectionLabel(selectedTypes, dropdownOptions.types)
                  ?? (selectedTypes.length === dropdownOptions.types.length ? t('filters.allTypes') : selectedTypes.join(', ') || t('filters.selectTypes'))}
                onClick={() => setTypeModalOpen(true)}
              compact={forceFiltersOpen}
              />
            )}

            {dropdownOptions.varieties.length > 0 && (
              <FilterDropdown
                label={t('filters.variety')}
                value={singleSelectionLabel(selectedVarieties, dropdownOptions.varieties)
                  ?? (selectedVarieties.length === dropdownOptions.varieties.length ? t('filters.allVarieties') : t('filters.nSelected', { count: selectedVarieties.length }))}
                onClick={() => setVarietyModalOpen(true)}
              compact={forceFiltersOpen}
              />
            )}

            {dropdownOptions.sizes.length > 0 && (
              <FilterDropdown
                label={t('filters.size')}
                value={singleSelectionLabel(selectedSizes, dropdownOptions.sizes)
                  ?? (selectedSizes.length === dropdownOptions.sizes.length ? t('filters.allSizes') : t('filters.nSelected', { count: selectedSizes.length }))}
                onClick={() => setSizeModalOpen(true)}
              compact={forceFiltersOpen}
              />
            )}

            {dropdownOptions.grades.length > 0 && (
              <FilterDropdown
                label={t('filters.grade')}
                value={singleSelectionLabel(selectedGrades, dropdownOptions.grades)
                  ?? (selectedGrades.length === dropdownOptions.grades.length ? t('filters.allGrades') : t('filters.nSelected', { count: selectedGrades.length }))}
                onClick={() => setGradeModalOpen(true)}
              compact={forceFiltersOpen}
              />
            )}

            {dropdownOptions.packages.length > 0 && (
              <FilterDropdown
                label={t('filters.sku')}
                value={singleSelectionLabel(selectedPackages, dropdownOptions.packages)
                  ?? (selectedPackages.length === dropdownOptions.packages.length ? t('filters.allSkus') : t('filters.nSelected', { count: selectedPackages.length }))}
                onClick={() => setPackageModalOpen(true)}
              compact={forceFiltersOpen}
              />
            )}
            </PresetFilterStrip>
            </div>
          )}
        </div>

        {/* Cards */}
        {presetScorecards ? <div className="flex-shrink-0 empty:hidden">{presetScorecards}</div> : null}

        {/* Chart */}
        <div className={`flex-1 min-h-0 flex flex-col overflow-hidden${fsFill} bg-white dark:bg-[#191919] rounded-lg`}>
          {presetDescription ? (
            <div className={`${PRESET_PLOT_CAPTION_PAD} flex-shrink-0`}>
              <p className="text-[12px] text-[#828382] dark:text-gray-400 text-center">
                {(() => {
                  const commodityLabel = commodity
                    ? commodity
                        .split(' ')
                        .filter(Boolean)
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ')
                    : 'this commodity';

                  const isMeaningful = (v) => v != null && v !== '' && v !== 'N/A' && v !== 'Not Classified';
                  const parts = [];

                  // Commodity attributes only (year/week, origins, terminals, price type intentionally omitted)
                  if (dropdownOptions.types?.length && selectedTypes.length === 1) {
                    if (isMeaningful(selectedTypes[0])) parts.push(selectedTypes[0]);
                  }
                  if (dropdownOptions.varieties?.length && selectedVarieties.length === 1) {
                    if (isMeaningful(selectedVarieties[0])) parts.push(selectedVarieties[0]);
                  }
                  if (dropdownOptions.sizes?.length && selectedSizes.length === 1) {
                    if (isMeaningful(selectedSizes[0])) parts.push(selectedSizes[0]);
                  }
                  if (dropdownOptions.grades?.length && selectedGrades.length === 1) {
                    if (isMeaningful(selectedGrades[0])) parts.push(selectedGrades[0]);
                  }
                  if (dropdownOptions.packages?.length && selectedPackages.length === 1) {
                    if (isMeaningful(selectedPackages[0])) parts.push(selectedPackages[0]);
                  }

                  const summary = parts.filter(Boolean).join(' · ');
                  return summary ? `${commodityLabel} · ${summary}` : commodityLabel;
                })()}
              </p>
            </div>
          ) : null}
          <div className={`flex-1 min-h-0 overflow-y-auto${fsFill} px-4`}>
          {/* Week Selector - Web Optimized with Arrow Navigation */}
          <div className="py-2" ref={weekSelectorRef}>
            <p className="font-medium mb-2" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>{t('charts.smartPriceTracker.selectWeek')}</p>
            <div className="flex items-center gap-2">
              {/* Left Arrow */}
              <button
                onClick={() => scrollWeeks(-1)}
                className={`flex-shrink-0 p-2 rounded-lg bg-white dark:bg-[#282A29] border border-[#E8EBE9] dark:border-[#3a3c3b] text-[#828382] dark:text-gray-400
                           hover:border-bloom-green dark:hover:border-green-500/50 transition-colors duration-200`}
              >
                <ChevronLeft size={20} />
              </button>

              {/* Visible Weeks Container - buttons fill available space */}
              <div className="flex gap-2 flex-1">
                {visibleWeeks.map((week) => {
                  const isFuture = isWeekInFuture(week.weekNumber, week.year);
                  const isSelected = selectedWeek === week.weekNumber;

                  return (
                    <button
                      key={week.weekNumber}
                      onClick={() => setSelectedWeek(week.weekNumber)}
                      style={{ fontSize: fontSize - 2 }}
                      className={`
                        flex-1 px-2 py-2 rounded-full font-medium text-center
                        transition-colors duration-200 min-w-0
                        ${isSelected
                          ? isFuture
                            ? 'bg-[#b5cd92] text-white'
                            : 'bg-bloom-green text-white'
                          : isFuture
                            ? isDark
                              ? 'bg-[#282A29] border border-dashed border-gray-600 text-gray-500 opacity-60'
                              : 'bg-white border border-dashed border-[#828382] text-[#828382] opacity-60'
                            : isDark
                              ? 'bg-[#282A29] border border-[#3a3c3b] text-gray-400 hover:border-green-500/50'
                              : 'bg-white border border-[#E8EBE9] text-[#828382] hover:border-bloom-green'
                        }
                      `}
                    >
                      {t('charts.smartPriceTracker.week')} {week.weekNumber}
                    </button>
                  );
                })}
              </div>

              {/* Right Arrow */}
              <button
                onClick={() => scrollWeeks(1)}
                className={`flex-shrink-0 p-2 rounded-lg bg-white dark:bg-[#282A29] border border-[#E8EBE9] dark:border-[#3a3c3b] text-[#828382] dark:text-gray-400
                           hover:border-bloom-green dark:hover:border-green-500/50 transition-colors duration-200`}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          {/* Selected Week Display */}
          {selectedWeekInfo && (
            <div className="bg-white dark:bg-[#222321] border border-[#E8EBE9] dark:border-transparent rounded-lg py-2 text-center mb-2">
              <p className="text-[#282A29] dark:text-white font-semibold" style={{ fontSize }}>
                {t('charts.smartPriceTracker.week').toUpperCase()} {selectedWeek}: {formatDateRange(selectedWeekInfo.startDate, selectedWeekInfo.endDate)}
              </p>
            </div>
          )}

          {/* Price Category Cards */}
          <div className={`space-y-2 ${forceFiltersOpen && !presetSingleChartFullscreen ? '' : 'pb-2'}`}>
            {categoryConfig.map(({ key, titleKey, borderColor, bgColor, priceColor }) => (
              <div
                key={key}
                className="rounded-lg overflow-hidden"
                style={{ border: `2px solid ${borderColor}`, backgroundColor: bgColor }}
              >
                <button
                  onClick={() => setCardExpanded((prev) => ({ ...prev, [key]: !prev[key] }))}
                  className="w-full flex items-center justify-between p-3"
                >
                  <span className={`${isDark ? 'text-white' : 'text-[#282A29]'} font-bold`} style={{ fontSize }}>{t(titleKey)}</span>
                  <div className="flex items-center gap-2">
                    <span className={isDark ? 'text-white/70' : 'text-[#828382]'} style={{ fontSize: fontSize - 2 }}>
                      {weekData[key].length} {weekData[key].length !== 1 ? t('charts.smartPriceTracker.items') : t('charts.smartPriceTracker.item')}
                    </span>
                    {cardExpanded[key] ? (
                      <ChevronUp size={16} className={isDark ? 'text-white' : 'text-[#282A29]'} />
                    ) : (
                      <ChevronDown size={16} className={isDark ? 'text-white' : 'text-[#282A29]'} />
                    )}
                  </div>
                </button>

                {cardExpanded[key] && (
                  <div className="px-3 pb-3">
                    {weekData[key].length > 0 ? (
                      weekData[key].map((item, index) => (
                        <div
                          key={index}
                          className={`flex items-center justify-between py-1.5 border-b ${isDark ? 'border-white/10' : 'border-[#E8EBE9]'} last:border-b-0`}
                        >
                          <div className="flex-1">
                            <span className={isDark ? 'text-white/80' : 'text-[#282A29]'} style={{ fontSize: fontSize - 2 }}>
                              {formatItemLabel(item)}
                            </span>
                          </div>
                          <span className="font-bold" style={{ color: priceColor, fontSize }}>
                            ${item.avg_price.toFixed(2)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-[#828382] dark:text-gray-400 text-center py-2 text-xs">{t('charts.smartPriceTracker.noItems')}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <FilterModal
        isOpen={yearModalOpen}
        onClose={() => setYearModalOpen(false)}
        title={t('filters.selectYear')}
        options={availableYears.map(String)}
        selectedValues={String(selectedYear)}
        onSelect={(value) => {
          setSelectedYear(Number(value));
        }}
      />

      <FilterModal
        isOpen={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        title={t('filters.selectTypes')}
        options={dropdownOptions.types}
        selectedValues={selectedTypes}
        onSelect={(type) => handleMultiSelect(type, selectedTypes, setSelectedTypes)}
        multiSelect
        onSelectAll={() => setSelectedTypes(dropdownOptions.types)}
        onClearAll={() => setSelectedTypes([])}
      />

      <FilterModal
        isOpen={varietyModalOpen}
        onClose={() => setVarietyModalOpen(false)}
        title={t('filters.selectVarieties')}
        options={dropdownOptions.varieties}
        selectedValues={selectedVarieties}
        onSelect={(v) => handleMultiSelect(v, selectedVarieties, setSelectedVarieties)}
        multiSelect
        onSelectAll={() => setSelectedVarieties(dropdownOptions.varieties)}
        onClearAll={() => setSelectedVarieties([])}
      />

      <FilterModal
        isOpen={sizeModalOpen}
        onClose={() => setSizeModalOpen(false)}
        title={t('filters.selectSizes')}
        options={dropdownOptions.sizes}
        selectedValues={selectedSizes}
        onSelect={(s) => handleMultiSelect(s, selectedSizes, setSelectedSizes)}
        multiSelect
        onSelectAll={() => setSelectedSizes(dropdownOptions.sizes)}
        onClearAll={() => setSelectedSizes([])}
      />

      <FilterModal
        isOpen={gradeModalOpen}
        onClose={() => setGradeModalOpen(false)}
        title={t('filters.selectGrades')}
        options={dropdownOptions.grades}
        selectedValues={selectedGrades}
        onSelect={(g) => handleMultiSelect(g, selectedGrades, setSelectedGrades)}
        multiSelect
        onSelectAll={() => setSelectedGrades(dropdownOptions.grades)}
        onClearAll={() => setSelectedGrades([])}
      />

      <FilterModal
        isOpen={packageModalOpen}
        onClose={() => setPackageModalOpen(false)}
        title={t('filters.selectSkus')}
        options={dropdownOptions.packages}
        selectedValues={selectedPackages}
        onSelect={(p) => handleMultiSelect(p, selectedPackages, setSelectedPackages)}
        multiSelect
        onSelectAll={() => setSelectedPackages(dropdownOptions.packages)}
        onClearAll={() => setSelectedPackages([])}
      />
    </div>
  );
};

export default ClusterChart;

